/**
 * Shared utility functions for ClickToFly calculator
 * Used by both Card View and Table View
 * Updated for ClickToFlyResult model structure
 */

/**
 * Format currency value
 * @param {number} amount - Amount to format
 * @returns {string} Formatted currency string (e.g., "$19,350")
 */
export const formatCurrency = (amount) => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

/**
 * Format percentage value
 * @param {number} decimal - Decimal value to format (e.g., 0.05 for 5%)
 * @returns {string} Formatted percentage string (e.g., "5.0%")
 */
export const formatPercent = (decimal) => {
  return `${(Math.abs(decimal) * 100).toFixed(1)}%`;
};

/**
 * Calculate total discount from selected options
 * Uses new ClickToFlyResult change object format
 * Supports per-leg departure time discounts (OW uses leg1 only, RT uses both)
 * @param {Object} selectedOptions - Selected toggle options
 * @param {number} defaultPrice - Default price
 * @returns {Object} Total discount info { percent, dollars }
 */
export const getTotalDiscount = (selectedOptions, defaultPrice) => {
  let totalPercent = 0;

  // Apply member discount (change.percent is negative for discounts)
  if (selectedOptions.member?.change) {
    totalPercent += Math.abs(selectedOptions.member.change.percent);
  }

  // Apply TDR discount
  if (selectedOptions.tdr?.change) {
    totalPercent += Math.abs(selectedOptions.tdr.change.percent);
  }

  // Apply airport radius discount
  if (selectedOptions.airportRadius?.change) {
    totalPercent += Math.abs(selectedOptions.airportRadius.change.percent);
  }

  // Apply YOM adjustment (can be positive for increases or negative for discounts)
  if (selectedOptions.yom?.change) {
    // For YOM: negative percent = discount, positive = increase
    totalPercent -= selectedOptions.yom.change.percent;
  }

  // Apply time flexibility discount (leg-based: OW uses leg1 only, RT uses both)
  if (selectedOptions.departureTimeLeg1?.change) {
    totalPercent += Math.abs(selectedOptions.departureTimeLeg1.change.percent);
  }
  if (selectedOptions.departureTimeLeg2?.change) {
    totalPercent += Math.abs(selectedOptions.departureTimeLeg2.change.percent);
  }

  return {
    percent: totalPercent,
    dollars: defaultPrice * totalPercent,
  };
};

/**
 * Calculate final price based on selected options
 * Uses new ClickToFlyResult model structure
 * Supports per-leg departure time discounts (OW uses leg1 only, RT uses both)
 * @param {Object} aircraft - Aircraft pricing data from ClickToFlyResult
 * @param {Object} selectedOptions - Selected toggle options
 * @returns {number} Calculated final price
 */
export const calculateFinalPrice = (aircraft, selectedOptions) => {
  const defaultPrice = aircraft.defaultPrice;
  let totalDiscount = 0;

  // Apply member discount (change.percent is negative for discounts)
  if (selectedOptions.member?.change) {
    totalDiscount += Math.abs(selectedOptions.member.change.percent);
  }

  // Apply TDR discount
  if (selectedOptions.tdr?.change) {
    totalDiscount += Math.abs(selectedOptions.tdr.change.percent);
  }

  // Apply airport radius discount
  if (selectedOptions.airportRadius?.change) {
    totalDiscount += Math.abs(selectedOptions.airportRadius.change.percent);
  }

  // Apply YOM adjustment (can be positive for increases or negative for discounts)
  if (selectedOptions.yom?.change) {
    // For YOM: negative percent = discount (add to discount), positive = increase (subtract from discount)
    totalDiscount -= selectedOptions.yom.change.percent;
  }

  // Apply time flexibility discount (leg-based: OW uses leg1 only, RT uses both)
  if (selectedOptions.departureTimeLeg1?.change) {
    totalDiscount += Math.abs(selectedOptions.departureTimeLeg1.change.percent);
  }
  if (selectedOptions.departureTimeLeg2?.change) {
    totalDiscount += Math.abs(selectedOptions.departureTimeLeg2.change.percent);
  }

  let finalPrice = defaultPrice * (1 - totalDiscount);

  // Apply WiFi add-on cost if enabled (add-on increases price)
  if (selectedOptions.wifiEnabled && selectedOptions.yom?.wifi?.available) {
    const wifiAddOnPercent = selectedOptions.yom.wifi.addOnPercent || 0;
    finalPrice += defaultPrice * wifiAddOnPercent;
  }

  return isNaN(finalPrice) ? defaultPrice : finalPrice;
};

/**
 * Get default selected options for an aircraft
 * Uses new ClickToFlyResult model structure with toggleOptions
 * @param {Object} aircraft - Aircraft data from ClickToFlyResult
 * @returns {Object} Default selected options
 */
export const getDefaultSelectedOptions = (aircraft) => {
  const memberToggle = aircraft.toggleOptions?.member;
  const isMember = memberToggle?.isMember || false;

  // Use the appropriate discount based on member status
  const defaultMemberOption = isMember
    ? {
        label: "Member",
        value: true,
        change: memberToggle?.memberDiscount || { percent: 0, dollars: 0 },
        isCurrent: true,
      }
    : {
        label: "Non-Member",
        value: false,
        change: memberToggle?.nonMemberDiscount || { percent: 0, dollars: 0 },
        isCurrent: true,
      };

  return {
    member: defaultMemberOption,
    tdr: null,
    airportRadius: null,
    yom: null,
    departureTimeLeg1: null,
    departureTimeLeg2: null,
    wifiEnabled: false,
  };
};

/**
 * Get human-readable seat count
 * @param {Object} aircraft - Aircraft data
 * @returns {string} Seat count (e.g., "6" or "N/A")
 */
export const getSeatCount = (aircraft) => {
  if (aircraft.seatCount) {
    return `${aircraft.seatCount}`;
  }
  return "N/A";
};
